/**
 * Shared Firebase Web SDK client. Used by every monorepo app that authenticates
 * users (brand, admin). Reads the FIREBASE_* runtime config (see runtimeConfig).
 *
 * The Go backend verifies Firebase ID tokens for ALL roles via
 * `internal/middleware/auth.go:RequireAuth`. Role enforcement (admin vs brand
 * vs creator) is per-route via `RequireRole`. So every FE app uses the same
 * Firebase project, just signs in different users.
 */
import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  browserLocalPersistence,
  setPersistence,
  type Auth,
} from 'firebase/auth';
import { getFirestore, type Firestore } from 'firebase/firestore';
import {
  getAnalytics,
  isSupported,
  type Analytics,
} from 'firebase/analytics';
import { runtimeConfig, type RuntimeConfigKey } from '../config/runtimeConfig';

// GA4 is switched off: the code and measurement-id wiring stay so it can be turned back on,
// but nothing initialises analytics, so no GA traffic is sent.
const ANALYTICS_ENABLED = false;

// Exported so callers that need a throwaway, non-primary Firebase App
// instance (e.g. AccountSection's Google-link flow — obtaining a Google
// id_token without touching the primary signed-in session) can initialize
// one with the same project config.
export const firebaseConfig = {
  apiKey: runtimeConfig('FIREBASE_API_KEY'),
  authDomain: runtimeConfig('FIREBASE_AUTH_DOMAIN'),
  projectId: runtimeConfig('FIREBASE_PROJECT_ID'),
  storageBucket: runtimeConfig('FIREBASE_STORAGE_BUCKET'),
  messagingSenderId: runtimeConfig('FIREBASE_MESSAGING_SENDER_ID'),
  appId: runtimeConfig('FIREBASE_APP_ID'),
  measurementId: runtimeConfig('FIREBASE_MEASUREMENT_ID'),
};

const requiredKeys: RuntimeConfigKey[] = [
  'FIREBASE_API_KEY',
  'FIREBASE_AUTH_DOMAIN',
  'FIREBASE_PROJECT_ID',
  'FIREBASE_STORAGE_BUCKET',
  'FIREBASE_MESSAGING_SENDER_ID',
  'FIREBASE_APP_ID',
];

const missingVars = requiredKeys
  .filter((key) => !runtimeConfig(key))
  .map((key) => `VITE_${key}`);

if (missingVars.length > 0 && import.meta.env.DEV) {
  console.error('Missing Firebase environment variables:', missingVars);
  console.error(
    'Please create a .env file with the required Firebase configuration.',
  );
}

let app!: FirebaseApp;
let auth!: Auth;
let db!: Firestore;
let analytics: Analytics | null = null;

try {
  app = (
    getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0]
  ) as FirebaseApp;
  auth = getAuth(app);
  setPersistence(auth, browserLocalPersistence).catch((err) => {
    console.error('Failed to set Firebase persistence:', err);
  });
  db = getFirestore(app);
  if (ANALYTICS_ENABLED && typeof window !== 'undefined') {
    isSupported().then((yes) => {
      if (yes) analytics = getAnalytics(app);
    });
  }
} catch (error) {
  console.error('Firebase initialization error:', error);
  if (import.meta.env.DEV) {
    console.error(
      'Firebase failed to initialize. Check your .env file and Firebase configuration.',
    );
  }
  throw error;
}

export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export { app, auth, db, analytics };
